// ============================================================================
// BOQ — scope / responsibility parties
// ============================================================================
// Named parties per project that own the design / specification of a scope
// (e.g. an MEP package carried by a separate services consultant). Lines
// reference one via project_items.scope_party_id; is_external marks a party
// as someone else's scope so the Summary can subtotal our scope vs others.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';

const mapParty = (p) => ({
  id: p.id,
  name: p.name,
  isExternal: p.is_external,
  sortOrder: p.sort_order,
  note: p.note,
});

export const useBoqScopeParties = (boqProjectId) => {
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!boqProjectId) { setParties([]); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const { data, error: fErr } = await supabase.schema('boq').from('scope_parties')
        .select('id, name, is_external, sort_order, note')
        .eq('project_id', boqProjectId).order('sort_order').order('name');
      if (fErr) throw fErr;
      setParties((data || []).map(mapParty));
    } catch (err) {
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'scope parties' }));
    } finally { setLoading(false); }
  }, [boqProjectId]);

  const addParty = useCallback(async (input) => {
    try {
      const nextOrder = parties.reduce((m, p) => Math.max(m, p.sortOrder), 0) + 1;
      const { data, error: insErr } = await supabase.schema('boq').from('scope_parties')
        .insert({
          project_id: boqProjectId,
          name: input.name?.trim() || 'Party',
          is_external: input.isExternal ?? true,
          sort_order: input.sortOrder ?? nextOrder,
        })
        .select('id, name, is_external, sort_order, note').single();
      if (insErr) throw insErr;
      setParties((prev) => [...prev, mapParty(data)].sort((a, b) => a.sortOrder - b.sortOrder));
      return { success: true, party: mapParty(data) };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'add', entity: 'scope party' }) };
    }
  }, [boqProjectId, parties]);

  const updateParty = useCallback(async (id, patch) => {
    try {
      const payload = {};
      if (patch.name !== undefined) payload.name = patch.name?.trim() || 'Party';
      if (patch.isExternal !== undefined) payload.is_external = patch.isExternal;
      const { data, error: uErr } = await supabase.schema('boq').from('scope_parties')
        .update(payload).eq('id', id).select('id, name, is_external, sort_order, note').single();
      if (uErr) throw uErr;
      setParties((prev) => prev.map((p) => (p.id === id ? mapParty(data) : p)));
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'scope party' }) };
    }
  }, []);

  const deleteParty = useCallback(async (id) => {
    try {
      const { error: dErr } = await supabase.schema('boq').from('scope_parties').delete().eq('id', id);
      if (dErr) throw dErr;
      setParties((prev) => prev.filter((p) => p.id !== id));
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'scope party' }) };
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return { parties, loading, error, refresh: fetchAll, addParty, updateParty, deleteParty };
};
